import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api, fmtMoney } from "@/lib/api";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import {
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  TrendUp,
} from "@phosphor-icons/react";

function KPI({ label, value, positive, testid, sub }) {
  return (
    <div
      data-testid={testid}
      className="flex flex-col justify-between border-r border-b border-border/60 bg-white p-6 h-full"
    >
      <div className="overline">{label}</div>
      <div className="mt-6">
        <div
          className={`mono text-3xl font-semibold tracking-tight ${
            positive === true
              ? "text-emerald-700"
              : positive === false
                ? "text-red-600"
                : "text-foreground"
          }`}
        >
          {value}
        </div>
        {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard")).data,
    refetchInterval: 15000,
  });
  const { data: last } = useQuery({
    queryKey: ["last-entry"],
    queryFn: async () => (await api.get("/last-entry")).data,
    refetchInterval: 15000,
  });

  if (isLoading || !data)
    return <div className="p-10 text-sm text-muted-foreground">Yükleniyor...</div>;

  const { kpis, trend, rates } = data;

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline">Genel Bakış</div>
          <h1 className="mt-1 text-3xl font-black tracking-tight">Nakit Akışı Paneli</h1>
          <div className="mt-2 text-xs text-muted-foreground" data-testid="last-entry-date">
            Son girdi:{" "}
            <span className="mono font-semibold text-foreground">
              {last?.last_entry_at
                ? new Date(last.last_entry_at).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
                : "—"}
            </span>
          </div>
        </div>
        <div className="rounded-md border border-border bg-white px-4 py-2 text-xs">
          <span className="overline mr-2">TCMB {rates.source_date}</span>
          <span className="mono mr-3">USD ₺{rates.USD?.toFixed(4)}</span>
          <span className="mono">EUR ₺{rates.EUR?.toFixed(4)}</span>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 border-l border-t border-border/60 sm:grid-cols-2 lg:grid-cols-4">
        <KPI
          label="Toplam Nakit (TL)"
          value={fmtMoney(kpis.cash_try)}
          testid="kpi-cash"
          sub="Banka bakiye toplamı"
        />
        <KPI
          label="Bu Ay Gelir"
          value={fmtMoney(kpis.income_month_try)}
          positive={true}
          testid="kpi-income"
        />
        <KPI
          label="Bu Ay Gider"
          value={fmtMoney(kpis.expense_month_try)}
          positive={false}
          testid="kpi-expense"
          sub="Yalnızca Giderler sayfası"
        />
        <KPI
          label="Bu Ay Net"
          value={fmtMoney(kpis.net_month_try)}
          positive={kpis.net_month_try >= 0}
          testid="kpi-net"
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-md border border-border bg-white p-6 lg:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="overline">Son 6 Ay</div>
              <div className="mt-1 text-lg font-semibold">
                Gelir vs Gider (TL)
              </div>
            </div>
            <TrendUp size={20} weight="duotone" className="text-primary" />
          </div>
          <div className="mt-4 h-72">
            <ResponsiveContainer>
              <LineChart data={trend}>
                <CartesianGrid stroke="#eee" strokeDasharray="0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₺${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => fmtMoney(v)} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="income"
                  name="Gelir"
                  stroke="hsl(158,79%,17%)"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
                <Line
                  type="monotone"
                  dataKey="expense"
                  name="Gider"
                  stroke="hsl(0,84%,60%)"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
                <Line
                  type="monotone"
                  dataKey="net"
                  name="Net"
                  stroke="hsl(38,92%,50%)"
                  strokeWidth={2}
                  strokeDasharray="4 4"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-md border border-border bg-white p-6" data-testid="kpi-upcoming-rec">
            <div className="flex items-center gap-2 overline">
              <ArrowDownRight size={12} /> Yaklaşan Tahsilatlar
            </div>
            <div className="mono mt-4 text-2xl font-semibold text-emerald-700">
              {fmtMoney(kpis.upcoming_receivables_try)}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">Bekleyen gelirler (TL)</div>
          </div>
          <div className="rounded-md border border-border bg-white p-6" data-testid="kpi-upcoming-pay">
            <div className="flex items-center gap-2 overline">
              <ArrowUpRight size={12} /> Yaklaşan Ödemeler
            </div>
            <div className="mono mt-4 text-2xl font-semibold text-red-600">
              {fmtMoney(kpis.upcoming_payments_try)}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">Bekleyen giderler (TL)</div>
          </div>
          <div className="rounded-md border border-border bg-white p-6">
            <div className="flex items-center gap-2 overline">
              <Wallet size={12} /> Aylık Karşılaştırma
            </div>
            <div className="mt-4 h-24">
              <ResponsiveContainer>
                <BarChart data={trend.slice(-3)}>
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => fmtMoney(v)} />
                  <Bar dataKey="income" fill="hsl(158,64%,52%)" />
                  <Bar dataKey="expense" fill="hsl(0,84%,60%)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
